import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Calendar, MapPin, Truck, Package } from 'lucide-react';
import type { Trip, TripStatus } from '@/types/trip';

const STATUS_COLOR: Record<TripStatus, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#FEF3C7', fg: '#92400E', label: 'بانتظار' },
  active:      { bg: '#DBEAFE', fg: '#1E40AF', label: 'نشطة' },
  in_progress: { bg: '#DDD6FE', fg: '#5B21B6', label: 'قيد التنفيذ' },
  completed:   { bg: '#BBF7D0', fg: '#166534', label: 'مكتملة' },
  cancelled:   { bg: '#FEE2E2', fg: '#991B1B', label: 'ملغاة' },
};

export function TripCard({ trip }: { trip: Trip }) {
  const { t } = useTranslation();
  const cfg = STATUS_COLOR[trip.status] || STATUS_COLOR.pending;
  const date = trip.departure_date ? new Date(trip.departure_date) : null;

  return (
    <Link
      to={`/trips/${trip.id}`}
      className="block bg-surface border border-border rounded-xl p-4 hover:border-primary hover:shadow-sm transition"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Truck size={14} className="text-primary" />
          <span>{trip.vehicle_brand || ''} {trip.vehicle_model || ''} {trip.plate_number ? `• ${trip.plate_number}` : ''}</span>
        </div>
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ background: cfg.bg, color: cfg.fg }}
        >
          {t(`trip_status.${trip.status}`, cfg.label)}
        </span>
      </div>
      <div className="flex items-center gap-2 text-text-primary mb-2">
        <MapPin size={14} className="text-primary" />
        <span>{trip.origin_city}</span>
        <ArrowLeft size={14} />
        <span className="font-semibold">{trip.destination_city}</span>
      </div>
      <div className="flex items-center justify-between text-sm text-text-secondary">
        {date && (
          <span className="flex items-center gap-1">
            <Calendar size={13} />
            {date.toLocaleDateString('ar-SA')}
          </span>
        )}
        <div className="flex items-center gap-3">
          {trip.shipment_count != null && (
            <span className="flex items-center gap-1">
              <Package size={13} /> {trip.shipment_count}
            </span>
          )}
          {trip.price_per_kg != null && (
            <span className="font-semibold text-secondary">
              {trip.price_per_kg} {trip.currency || 'USD'}/كغ
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
